import { apiUrl, authHeaders } from '@/lib/shell';

// D74 credit plans. Amounts named *Micros are credit micros: 1 credit = 1 000 000.
export const CREDIT_MICROS = 1_000_000;

export type BlockedReason = 'plan_required' | 'credits_exhausted' | 'member_limit_reached';
export type Surface = 'chat' | 'agents' | 'apps';
export const SURFACES: readonly Surface[] = ['chat', 'agents', 'apps'];

export interface PlanOption {
  id: string;
  credits: number;
  /** Monthly price in SEK, excluding VAT. */
  priceSek: number;
  orePerCredit: number;
  volumeDiscountPercent: number;
}

export interface PlanDiscount {
  percentOff: number;
  endsAt: string | null;
  code: string | null;
}

export interface BillingAccount {
  creditBalanceMicros: number;
  planId: string | null;
  planStatus: string;
  planActive: boolean;
  periodStart: string | null;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  nextPlanId: string | null;
  nextPlanAt: string | null;
  discount: PlanDiscount | null;
  taxCountry: string | null;
  hasCustomer: boolean;
}

export interface MemberLimit {
  limitMicros: number;
  usedMicros: number;
}

export interface BillingStatus {
  enabled: boolean;
  paymentsEnabled: boolean;
  canAsk: boolean;
  blockedReason: BlockedReason | null;
  status: string;
  offer: { kind: 'credit_plans'; currency: 'sek'; tax: 'exclusive' };
  account: BillingAccount;
  plans: PlanOption[];
  /** A plan held outside the catalogue (the operator-assigned demo plan). */
  assignedPlan?: { id: string; credits: number } | null;
  owner?: 'company';
  companyId?: string;
  companyName?: string;
  canManage?: boolean;
  canViewCompany?: boolean;
  hasCustomer?: boolean;
  changePlanAvailable?: boolean;
  portalAvailable?: boolean;
  memberLimit?: MemberLimit | null;
}

export function hasPaidAccess(status: BillingStatus | null): boolean {
  return Boolean(status?.enabled && status.canAsk);
}

const safeInteger = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value);

function validStatus(value: BillingStatus): boolean {
  return (
    typeof value?.enabled === 'boolean' &&
    typeof value.canAsk === 'boolean' &&
    typeof value.status === 'string' &&
    value.offer?.kind === 'credit_plans' &&
    Array.isArray(value.plans) &&
    value.plans.every((plan) => safeInteger(plan.credits) && safeInteger(plan.priceSek)) &&
    safeInteger(value.account?.creditBalanceMicros) &&
    typeof value.account.planActive === 'boolean'
  );
}

export async function fetchBilling(): Promise<BillingStatus> {
  const response = await fetch(apiUrl('/api/v1/billing'), {
    headers: authHeaders(),
    credentials: 'include',
    signal: AbortSignal.timeout(10000),
  });
  if (!response.ok) throw new Error('billing_unavailable');
  const value = await response.json();
  if (!validStatus(value)) throw new Error('billing_unavailable');
  return value;
}

/** A refusal the dialog can explain, such as an unknown code or a declined card. */
export class BillingActionError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

async function post<T>(path: string, body: unknown, timeout = 20000): Promise<T> {
  const response = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { ...authHeaders(), 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body ?? {}),
    signal: AbortSignal.timeout(timeout),
  });
  const value = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new BillingActionError(
      value?.code ?? value?.error ?? 'billing_unavailable',
      response.status,
    );
  return value as T;
}

function stripeUrl(url: unknown): string {
  const parsed = new URL(String(url));
  if (
    parsed.protocol !== 'https:' ||
    !['checkout.stripe.com', 'billing.stripe.com'].includes(parsed.hostname)
  )
    throw new Error('billing_unavailable');
  return parsed.href;
}

export type PlanTiming = 'now' | 'renewal';
export type PlanChangeResult =
  | { kind: 'checkout'; url: string }
  | { kind: 'switched' | 'scheduled' | 'unchanged' | 'discounted' };

/** Without a plan this returns a Stripe Checkout URL; otherwise the change is done. */
export async function changePlan(input: {
  planId: string;
  timing: PlanTiming;
  code?: string | null;
}): Promise<PlanChangeResult> {
  const result = await post<PlanChangeResult & { url?: string }>('/api/v1/billing/plan', {
    planId: input.planId,
    timing: input.timing,
    ...(input.code ? { code: input.code } : {}),
  });
  return result.kind === 'checkout' ? { kind: 'checkout', url: stripeUrl(result.url) } : result;
}

export interface CodeCheck {
  code: string;
  percentOff: number;
  /** Months the discount lasts; null means it does not end. */
  months: number | null;
}

export const checkCode = (code: string) => post<CodeCheck>('/api/v1/billing/code/check', { code });

/** "Use code on this plan": the discount applies from the next renewal. */
export const applyCode = (code: string) =>
  post<{ kind: 'discounted' }>('/api/v1/billing/code', { code });

export async function billingPortalUrl(): Promise<string> {
  const { url } = await post<{ url: string }>('/api/v1/billing/portal', {});
  return stripeUrl(url);
}

export interface PersonUsage {
  userId: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  creditMicros: number;
  surfaces: Record<Surface, number>;
  periodUsedMicros: number;
  monthlyLimitMicros: number | null;
}

export interface UsageOverview {
  from: string;
  to: string;
  asOf: string;
  scope: 'company' | 'personal';
  canViewCompany: boolean;
  canManage: boolean;
  focusUserId: string | null;
  account: BillingAccount;
  days: Array<{ day: string } & Record<Surface, number>>;
  unpricedEvents: number;
  balance: Array<{ day: string; balanceMicros: number }>;
  grants: Array<{
    at: string;
    kind: 'plan_invoice' | 'operator_grant' | 'legacy_conversion';
    creditMicros: number;
    planId: string | null;
    billingReason: string | null;
  }>;
  people: PersonUsage[];
  operations?: {
    hasMore: boolean;
    items: Array<{
      at: string;
      userId: string | null;
      kind: 'model' | 'tool' | 'connection' | 'speech' | 'storage' | 'other';
      surface: Surface;
      creditMicros: number | null;
    }>;
  };
}

export async function fetchUsageOverview(
  filters: { from?: string; to?: string; userId?: string | null } = {},
  signal?: AbortSignal,
): Promise<UsageOverview> {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.userId) params.set('userId', filters.userId);
  const response = await fetch(apiUrl(`/api/v1/usage?${params}`), {
    headers: authHeaders(),
    credentials: 'include',
    signal: signal ?? AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new BillingActionError(`usage_${response.status}`, response.status);
  return response.json();
}

/** Owner only: whole credits per plan period, or null to remove the limit. */
export async function setMemberLimit(userId: string, monthlyCredits: number | null) {
  const response = await fetch(apiUrl(`/api/v1/usage/limits/${encodeURIComponent(userId)}`), {
    method: 'PUT',
    headers: { ...authHeaders(), 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ monthlyCredits }),
    signal: AbortSignal.timeout(15000),
  });
  const value = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new BillingActionError(value?.error ?? 'usage_unavailable', response.status);
  return value as { userId: string; monthlyLimitMicros: number | null };
}

// Guard programmatic sends too; existing paid streams may still reconnect.
export function createBillingFetch(
  inner: typeof fetch,
  allow: () => boolean,
  rejected: () => void,
): typeof fetch {
  return async (input, init) => {
    const path = new URL(
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
      location.origin,
    ).pathname;
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    if (path === '/api/chat' && method.toUpperCase() === 'POST' && !allow()) {
      return new Response(JSON.stringify({ error: 'payment_required', code: 'payment_required' }), {
        status: 402,
        headers: { 'content-type': 'application/json' },
      });
    }
    const response = await inner(input, init);
    if (path === '/api/chat' && (response.status === 402 || response.status === 503)) {
      const body = await response
        .clone()
        .json()
        .catch(() => ({}));
      if (body.code === 'payment_required' || body.code === 'billing_unavailable') rejected();
    }
    return response;
  };
}
